import {Injectable} from '@angular/core';
import {BoiteLienDialogComponent} from "@components/admin/parametre/personnalisation/boite-lien/boite-lien-dialog/boite-lien-dialog.component";
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {Observable,of} from "rxjs";
import {BoiteLien} from "@domain/boitelien/boitelien";
import {BoiteLienHttpService} from "@services/admin/parametre/personnalisation/boite-lien-http.service";
import {Result,TypeCodeErreur} from "@domain/common/http/result";
import {catchError,first,map} from "rxjs/operators";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {ListView,TypeComparaison,TypeFilter} from "@domain/common/list-view";
import {BoiteLienItemComponent} from "@components/admin/parametre/personnalisation/boite-lien/boite-lien-item/boite-lien-item.component";
import {Sorting} from "@domain/common/list-view/sorting";

/**
 * Service de gestion de la boite à lien
 *
 * @author Lionel Gimenez
 * @date 18/07/2025
 */
@Injectable()
export class BoiteLienService {
    /** Modèle de la liste */
    listView: ListView<BoiteLien,BoiteLienItemComponent>;

    /**
     * Constructeur
     */
    constructor(
        private matDialog: MatDialog,
        private boiteLienHttp: BoiteLienHttpService,
        private toastrService: ToastrService,
        private translateService: TranslateService,
    ) {
    }

    /**
     * Initialisation de la liste
     */
    initListView(): void {
        //Initialisation de la liste
        this.listView = new ListView<BoiteLien,BoiteLienItemComponent>({
            uri: `/controller/BoiteLien/listeLien`,
            title: this.translateService.instant('admin.parametres.personnalisation.onglets.boiteLiens'),
            component: BoiteLienItemComponent,
            isFilter: true,
            defaultOrder: 'ordre',
            listeFilters: [
                {
                    clef: 'libelle',
                    title: this.translateService.instant('admin.parametres.personnalisation.boiteLien.libelle'),
                    isDefault: true,
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE]
                },{
                    clef: 'url',
                    title: this.translateService.instant('admin.parametres.personnalisation.boiteLien.url'),
                    isDefault: true,
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE]
                },{
                    clef: 'ordre',
                    title: this.translateService.instant('admin.parametres.personnalisation.boiteLien.ordre'),
                    typeComparaison: TypeComparaison[TypeComparaison.EQUAL]
                },{
                    clef: 'isActif',
                    title: this.translateService.instant('admin.parametres.personnalisation.boiteLien.actif'),
                    isDefault: false,
                    type: TypeFilter[TypeFilter.BOOLEAN],
                    typeComparaison: TypeComparaison[TypeComparaison.EQUAL]
                }
            ]
        });

        //Définition des colonnes de tri
        this.listView.columns = [
            {key: 'libelle',title: 'admin.parametres.personnalisation.boiteLien.libelle'},
            {key: 'ordre',title: 'admin.parametres.personnalisation.boiteLien.ordre'},
            {key: 'actif',title: 'admin.parametres.personnalisation.boiteLien.actif'},
        ];

        //Ajout du tri de la liste selon l'ordre voulu
        this.listView.sorting = new Sorting(this.listView.columns,"ordre");

        //Persistence des filtres
        this.listView.isPersistFilters = true;
        this.listView.className = 'BoiteLienItemComponent';
    }

    /**
     * On rafraichit la listView
     */
    refresh(): void {
        this.listView.refresh();
    }

    /**
     * Ouvre la modale pour la création/modification
     */
    openDialog(boiteLien: BoiteLien): Observable<boolean> {
        //Ouverture de la boîte de dialogue
        return this.matDialog.open(BoiteLienDialogComponent,{
            width: '80%',
            data: {
                boiteLien
            }
        }).afterClosed();
    }

    /**
     * Sauvegarde la boite à lien
     */
    save(boiteLien: BoiteLien): Observable<boolean> {
        return this.boiteLienHttp.save(boiteLien).pipe(
            first(),
            map((result: Result) => {
                //Si pas d'erreur
                if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                    this.toastrService.success(
                        this.translateService.instant('global.success.enregistrement')
                    );
                    //On refresh la listview
                    this.refresh();

                    return true;
                } else {
                    //On traite l'erreur
                    TypeCodeErreur.showError(
                        result.codeErreur,
                        this.translateService,
                        this.toastrService
                    );
                    return false;
                }
            }),
            catchError(() => {
                //On affiche l'erreur
                this.toastrService.error(
                    this.translateService.instant('global.errors.enregistrement')
                );
                return of(false);
            })
        );
    }

    /**
     * Supprime la boite à lien
     */
    delete(idLien: number) {
        return this.boiteLienHttp.delete(idLien).pipe(
            first(),
            map((result: Result) => {
                //Si pas d'erreur
                if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
                    this.toastrService.success(
                        this.translateService.instant('global.success.suppression')
                    );
                    //On refresh la listview
                    this.refresh();

                    return true;
                } else {
                    //On traite l'erreur
                    TypeCodeErreur.showError(
                        result.codeErreur,
                        this.translateService,
                        this.toastrService
                    );
                    return false;
                }
            }),
            catchError(() => {
                //On traite l'erreur
                this.toastrService.error(
                    this.translateService.instant('global.errors.suppression')
                );
                return of(false);
            })
        );
    }
}